import { ApiError } from './meetings.api'
import { MEETING_ERROR_CODES } from '../constants/meetings.constants'
import { formatOccurrenceDateTime, type Translate } from '../utils/recurrence'

/**
 * What a failed join attempt means for the person who opened the link.
 *
 * Extracted from `MeetingGuard` so the rule can be tested: the guard's own
 * branch is unreachable without a DOM, and this decision is the whole of the
 * bug it was written for.
 */
export type JoinFailure =
  /** Ask for the password. NOT a refusal — the credentials just were not sent. */
  | { kind: 'password' }
  /** The id is a webinar, which has its own entry path. */
  | { kind: 'webinar' }
  /**
   * Every seat is taken. Not a reason to leave: the preview says so in a
   * dialog and offers to try again, since a seat can free up at any moment.
   */
  | { kind: 'full' }
  /** Give up and say why, with this `meetings.errors.*` key. */
  | { kind: 'redirect'; messageKey: string }
  /**
   * A recurring series' link on a day it cannot open: say why and, when there
   * is one, when the next meeting is.
   */
  | { kind: 'series'; messageKey: string; nextStartTimeMs?: number }

/** Refusals of a series link; they carry the next meeting's time. */
const SERIES = new Set<string>([
  MEETING_ERROR_CODES.OCCURRENCE_OVER,
  MEETING_ERROR_CODES.OCCURRENCE_CANCELLED,
  MEETING_ERROR_CODES.OCCURRENCE_NOT_TODAY,
  MEETING_ERROR_CODES.SERIES_ENDED,
])

/** Codes we can name accurately. Anything else falls back to "not found". */
const NAMED = new Set<string>([
  MEETING_ERROR_CODES.MEETING_CANCELLED,
  MEETING_ERROR_CODES.MEETING_ENDED,
])

/**
 * `isKnownWebinar` is passed in rather than looked up, because the webinar list
 * is store state and this stays a pure function of its inputs.
 *
 * The guard pre-fetches credentials by joining with NO password, so a protected
 * meeting answers 401 INVALID_PASSWORD every single time. Treating that as a
 * failure is what told an invited participant their meeting did not exist —
 * while the host walked in, the server exempting them from the password check.
 * It is a question, and it is asked before anything else here, because a
 * meeting that wants a password is not missing, not a webinar, and not over.
 */
export function decideJoinFailure(err: unknown, isKnownWebinar: boolean): JoinFailure {
  const code = err instanceof ApiError ? err.code : null

  if (code === MEETING_ERROR_CODES.INVALID_PASSWORD) return { kind: 'password' }
  if (isKnownWebinar) return { kind: 'webinar' }
  if (code === MEETING_ERROR_CODES.MEETING_FULL) return { kind: 'full' }
  if (code && SERIES.has(code)) {
    const next = (err as ApiError).details?.nextStartTimeMs
    return {
      kind: 'series',
      messageKey: code,
      ...(typeof next === 'number' && Number.isFinite(next) ? { nextStartTimeMs: next } : {}),
    }
  }
  if (code && NAMED.has(code)) return { kind: 'redirect', messageKey: code }

  // Everything else, including a plain `NOT_FOUND` from the server and any
  // non-ApiError throw. Saying "we couldn't find it" about a meeting that was
  // cancelled is why the named set above exists; saying it about a genuinely
  // unknown failure is the honest answer.
  return { kind: 'redirect', messageKey: MEETING_ERROR_CODES.MEETING_NOT_FOUND }
}

/**
 * The sentence a refused series link shows, e.g. "Today's meeting is over.
 * Next meeting: Wed 30 Sep, 10:00 AM."
 *
 * The next meeting is named in the viewer's own time and language: they are
 * the one who has to turn up.
 */
export function describeSeriesRefusal(
  failure: Extract<JoinFailure, { kind: 'series' }>,
  t: Translate,
  language: string,
): string {
  const reason = t(`meetings.errors.${failure.messageKey}`)
  if (failure.nextStartTimeMs === undefined) return reason
  const when = formatOccurrenceDateTime(failure.nextStartTimeMs, language)
  const nextKey =
    failure.messageKey === MEETING_ERROR_CODES.OCCURRENCE_NOT_TODAY
      ? 'meetings.series.nextMeetingOnDay'
      : 'meetings.series.nextMeeting'
  return `${reason} ${t(nextKey, { when })}`
}

/** What the Join-with-Meeting-ID dialog does after the server refused a join check. */
export type JoinByIdOutcome =
  /** Go on to the meeting page, which asks for what is still missing (the password). */
  | { kind: 'proceed' }
  /** Stay in the dialog and show this sentence. */
  | { kind: 'error'; message: string }

/**
 * The dialog used to decide on the browser's local copy of the meeting, through
 * the mock `meetingsService.validateJoin`. That copy knows nothing of seats, and
 * it is only fetched when the browser has none: a copy that was stale, or saved
 * without an end time (which `normalizeRecord` reads as "now"), turned a meeting
 * that was simply full into "This meeting has already been completed." The
 * server is the one that knows, so its answer decides — the same refusals, in
 * the same words, as opening the meeting link.
 */
export function joinByIdOutcome(err: unknown, t: Translate, language: string): JoinByIdOutcome {
  // Network failure or the server's own error: we do not know why, so we do
  // not claim the meeting is missing.
  if (!(err instanceof ApiError) || err.status >= 500) {
    return { kind: 'error', message: t('meetings.couldNotJoinMeeting') }
  }
  const failure = decideJoinFailure(err, false)
  if (failure.kind === 'password') return { kind: 'proceed' }
  // The meeting page answers a full meeting with its dialog over the preview;
  // this dialog has no preview behind it, so it says so where the ID was typed.
  if (failure.kind === 'full') return { kind: 'error', message: t('meetings.errors.MEETING_FULL') }
  if (failure.kind === 'series') return { kind: 'error', message: describeSeriesRefusal(failure, t, language) }
  if (failure.kind === 'redirect') return { kind: 'error', message: t(`meetings.errors.${failure.messageKey}`) }
  return { kind: 'proceed' }
}
